// Narration playback plus "syncs to paragraphs" for the composer. The composer
// calls this hook and hands the result to <NarrationCard> and to its block
// list: while `syncing`, tapping a paragraph calls stamp(id), which marks that
// paragraph as starting at the current playback time. The signature is a
// contract with the composer; fields may be added, never removed.
//
// Marks stay in reading order. The newest stamp wins: stamping a paragraph
// clears any other mark that would now be out of sequence (an earlier
// paragraph marked later than this one, or a later paragraph marked earlier),
// and those go back to being estimated. Marks for deleted blocks are pruned
// as soon as the block list changes.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Block, Narration } from './types'
import { activeBlockAt, buildTimeline, isNarrated, isPieceNarration, narrationTiming, pruneMarks, type TimelineEntry } from './narration'
import type { AudioPlayer } from './useAudioPlayer'
import { useNarrationPlayer } from './useNarrationPlayer'

export interface NarrationSync {
  player: AudioPlayer
  timeline: TimelineEntry[]
  /** The block being read right now (while playing or scrubbed), else null. */
  activeBlockId: string | null
  /** Sync mode: playing back while the author taps paragraphs as they're read. */
  syncing: boolean
  startSync(): void
  stopSync(): void
  /** Stamp `blockId` as starting at the current playback time. */
  stamp(blockId: string): void
  /** Clear one block's mark (back to estimated). */
  unstamp(blockId: string): void
  /** Narrated blocks (headings, paragraphs, quotes, thoughts, scene lines with text). */
  narratedCount: number
  /** How many of those the author has stamped. */
  markedCount: number
  isMarked(blockId: string): boolean
  /** Drop every mark (all timing back to estimated). */
  clearMarks(): void
  /** Seek to where `blockId` is read and play ("listen from here"). */
  playFrom(blockId: string): void
}

/** Seconds are stored to a tenth; finer is noise from tap timing. */
const round = (s: number) => Math.round(s * 10) / 10

/**
 * `marks` with `blockId` stamped at `seconds`, dropping marks that would put
 * narrated blocks out of order. Marks on blocks that aren't currently narrated
 * (e.g. a paragraph emptied mid-edit) are left alone.
 */
export function stampMark(blocks: Block[], marks: Record<string, number>, blockId: string, seconds: number): Record<string, number> {
  const order = blocks.filter(isNarrated).map((b) => b.id)
  const at = order.indexOf(blockId)
  if (at < 0) return marks
  const t = round(Math.max(0, seconds))
  const next: Record<string, number> = {}
  for (const [id, m] of Object.entries(marks)) {
    if (id === blockId) continue
    const i = order.indexOf(id)
    if (i >= 0 && ((i < at && m > t) || (i > at && m < t))) continue
    next[id] = m
  }
  next[blockId] = t
  return next
}

export function useNarrationSync(
  narration: Narration | null,
  blocks: Block[],
  onChange: (next: Narration | null) => void,
): NarrationSync {
  // Either kind of narration: one file, or pieces played back to back.
  const player = useNarrationPlayer(narration)
  const [syncing, setSyncing] = useState(false)
  const duration = player.duration || narration?.duration || 0

  // Pieces time themselves (each clip's block starts where the clip does).
  const timeline = useMemo(
    () => (narration ? buildTimeline(blocks, { duration, marks: narrationTiming(narration).marks }) : []),
    [narration, blocks, duration],
  )

  // Latest values for the callbacks, so rapid taps between renders each build
  // on the previous stamp instead of a stale narration.
  const narrationRef = useRef(narration)
  narrationRef.current = narration
  const blocksRef = useRef(blocks)
  blocksRef.current = blocks
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const playerRef = useRef(player)
  playerRef.current = player
  const timelineRef = useRef(timeline)
  timelineRef.current = timeline

  const commit = useCallback((next: Narration | null) => {
    narrationRef.current = next
    onChangeRef.current(next)
  }, [])

  // Prune marks for blocks that were deleted (or dropped by a restore). An
  // empty block list means "not loaded", not "everything deleted".
  useEffect(() => {
    // Pieces keep their marks with their clips (derived again on save).
    if (!narration || blocks.length === 0 || isPieceNarration(narration)) return
    const marks = narration.marks ?? {}
    const pruned = pruneMarks(blocks, marks)
    if (Object.keys(pruned).length !== Object.keys(marks).length) commit({ ...narration, marks: pruned })
  }, [narration, blocks, commit])

  // Sync ends with the recording, and can't outlive the narration.
  useEffect(() => {
    if (!syncing) return
    if (!narration) setSyncing(false)
    else if (!player.playing && duration > 0 && player.time >= duration - 0.25) setSyncing(false)
  }, [syncing, narration, player.playing, player.time, duration])

  const stamp = useCallback(
    (blockId: string) => {
      const n = narrationRef.current
      // Pieces are synced by their clips; there is nothing to stamp.
      if (!n || isPieceNarration(n)) return
      const p = playerRef.current
      const now = p.currentTime?.() ?? p.time
      const seconds = Math.min(now, p.duration || n.duration || now)
      const current = n.marks ?? {}
      const marks = stampMark(blocksRef.current, current, blockId, seconds)
      if (marks !== current) commit({ ...n, marks })
    },
    [commit],
  )

  const unstamp = useCallback(
    (blockId: string) => {
      const n = narrationRef.current
      if (!n || isPieceNarration(n) || !(blockId in (n.marks ?? {}))) return
      const marks = { ...n.marks }
      delete marks[blockId]
      commit({ ...n, marks })
    },
    [commit],
  )

  const clearMarks = useCallback(() => {
    const n = narrationRef.current
    if (n && Object.keys(n.marks ?? {}).length > 0) commit({ ...n, marks: {} })
  }, [commit])

  // Start from the top, or — when some paragraphs are already marked — from
  // the start of the last marked paragraph before the first unmarked one, so
  // the author hears a lead-in and taps the next paragraph as it begins.
  const startSync = useCallback(() => {
    if (!narrationRef.current) return
    const tl = timelineRef.current
    const firstUnmarked = tl.findIndex((e) => !e.marked)
    const from = firstUnmarked > 0 ? tl[firstUnmarked - 1].start : 0
    const p = playerRef.current
    p.seek(from)
    p.play()
    setSyncing(true)
  }, [])

  const stopSync = useCallback(() => {
    playerRef.current.pause()
    setSyncing(false)
  }, [])

  const playFrom = useCallback((blockId: string) => {
    const entry = timelineRef.current.find((e) => e.id === blockId)
    if (!entry) return
    const p = playerRef.current
    p.seek(entry.start)
    p.play()
  }, [])

  const isMarked = useCallback((blockId: string) => {
    const n = narrationRef.current
    const m = n ? narrationTiming(n).marks[blockId] : undefined
    return typeof m === 'number' && Number.isFinite(m)
  }, [])

  const activeBlockId = player.playing || player.time > 0 ? activeBlockAt(timeline, player.time) : null
  const markedCount = timeline.reduce((n, e) => n + (e.marked ? 1 : 0), 0)

  return {
    player,
    timeline,
    activeBlockId,
    syncing,
    startSync,
    stopSync,
    stamp,
    unstamp,
    narratedCount: narration ? blocks.filter(isNarrated).length : 0,
    markedCount,
    isMarked,
    clearMarks,
    playFrom,
  }
}
